<script>  
  import { setContext, onMount } from 'svelte';
  import { on_paste, size_var_to_px } from '#lib/util.js';
  import SiteSettingsModel from '#lib/SiteSettingsModel.svelte.js';
  import { load_popover_polyfill } from '#lib/popover_loader.js';
  import { page } from '$app/state';
  import { onNavigate } from '$app/navigation';
  import { dev } from '$app/env';
  import { handle_click_outside, handle_escape } from '#lib/click_outside.svelte.js';
  import { track_screen_height } from '#lib/screen_height.js';
  import * as env from "$app/env/public";

  // system styles
  import '#lib/system-styles/browser-consistency.css';
  import '#lib/system-styles/spacing.css';
  import '#lib/system-styles/layout.css';
  import '#lib/system-styles/typography.css';
  import '#lib/system-styles/colors.css';
  import '#lib/system-styles/locales.css';
  import '#lib/system-styles/borders.css';
  import '#lib/system-styles/box-shadows.css';
  import '#lib/system-styles/drop-shadows.css';
  import '#lib/system-styles/animations.css';
  import '#lib/system-styles/editing.css';
  import '#lib/system-styles/buttons.css';
  import '#lib/system-styles/forms.css';
  import '#lib/system-styles/cursors.css';
  import '#lib/system-styles/lists.css';
  
  let { data, children } = $props();

  // svelte-ignore state_referenced_locally (data.site_settings is stable during layout lifecycle)
  let site_settings = $state(new SiteSettingsModel(data.site_settings));
  setContext('site_settings', site_settings);

  const style_tag_font_faces = $derived([
    '<style lang="ignorepreprocess">',
      site_settings.typography.generate_font_faces(),
    '</style>'
  ].join(''));
  
  const style_tag_colors = $derived([
    '<style lang="ignorepreprocess">',
      ':root{',
      site_settings.colors?.compute_css_site_color_variables() || '', 
      '}',
    '</style>'
  ].join(''));
  
  const style_tag_typography_variables = $derived([
    '<style lang="ignorepreprocess">',
      ':root{',
        site_settings.typography.generate_typography_variables(site_settings.nav_items),
      '}',
    '</style>'
  ].join(''));

  const style_tag_dynamic_island = $derived.by(() => {

    const nav_layout_metrics = data.nav_layout_metrics;
    const MAIN_MENU_WIDTH = nav_layout_metrics.total_width;

    // TODO: Determine the tools width also dynamically, not hard coded:
    const ROW_1_RIGHT_TOOLS_WIDTH = page.data.current_user?.role === 'admin' ? 210 : page.data.current_user ? 84 : 100;
    const PAGE_INLINE_SPACING_MAX_CSS_VAR = site_settings.typography.page_inline_spacing_max;
    // Only apply the page inline spacing max to the floating menu variant
    // const PAGE_INLINE_SPACING_MAX_PIXEL = site_settings.menu_placement.includes('floating') ? size_var_to_px(PAGE_INLINE_SPACING_MAX_CSS_VAR) : 0;
    const PAGE_INLINE_SPACING_MAX_PIXEL = size_var_to_px(PAGE_INLINE_SPACING_MAX_CSS_VAR);
    const MAIN_MENU_ERROR_MARGIN = 30;
    const DYNAMIC_ISLAND_MIN_CONTENT_WIDTH = MAIN_MENU_WIDTH + ROW_1_RIGHT_TOOLS_WIDTH + 2 * PAGE_INLINE_SPACING_MAX_PIXEL - MAIN_MENU_ERROR_MARGIN;
    
    // Convert server-calculated navigation-space centering threshold to container-width threshold
    // The server provides balanced_threshold in navigation space, but container queries measure the full dynamic island width
    const server_nav_centering_threshold = nav_layout_metrics?.balanced_threshold || MAIN_MENU_WIDTH;
    
    // Convert navigation space requirement to dynamic island container width requirement
    // by adding the same elements that make up DYNAMIC_ISLAND_MIN_CONTENT_WIDTH
    const PRECISE_CENTERING_THRESHOLD = Math.max(
      DYNAMIC_ISLAND_MIN_CONTENT_WIDTH,
      server_nav_centering_threshold + ROW_1_RIGHT_TOOLS_WIDTH + 2 * PAGE_INLINE_SPACING_MAX_PIXEL - MAIN_MENU_ERROR_MARGIN
    );
    
    // Log dynamic island calculation variables
    // console.table({
    //   'MAIN_MENU_WIDTH': MAIN_MENU_WIDTH,
    //   'ROW_1_RIGHT_TOOLS_WIDTH': ROW_1_RIGHT_TOOLS_WIDTH,
    //   'PAGE_INLINE_SPACING_MAX_CSS_VAR': PAGE_INLINE_SPACING_MAX_CSS_VAR,
    //   'PAGE_INLINE_SPACING_MAX_PIXEL': PAGE_INLINE_SPACING_MAX_PIXEL,
    //   'MAIN_MENU_ERROR_MARGIN': MAIN_MENU_ERROR_MARGIN,
    //   'DYNAMIC_ISLAND_MIN_CONTENT_WIDTH': DYNAMIC_ISLAND_MIN_CONTENT_WIDTH,
    //   'SERVER_NAV_CENTERING_THRESHOLD': server_nav_centering_threshold,
    //   'PRECISE_CENTERING_THRESHOLD': PRECISE_CENTERING_THRESHOLD,
    //   'GROUP_WIDTHS': nav_layout_metrics?.group_widths
    // });

    
    
    // Use the balanced threshold for when to apply flex-basis: 0 to nav groups
    // const BALANCED_THRESHOLD = nav_layout_metrics?.balanced_threshold || MAIN_MENU_WIDTH;
    
    return [
      '<style lang="ignorepreprocess">',
        ':root{',
          '--dynamic-island-track-start:', site_settings.menu_track_start,';',
          '--dynamic-island-track-end:', site_settings.menu_track_end, ';',
          '--dynamic-island-main-menu-width:', MAIN_MENU_WIDTH, 'px;',
          '--dynamic-island-elevation:', site_settings.menu_elevation, ';',

          '.dynamic-island {',

            // We are using container queries (not media queries) to determine the mobile breakpoint for the dynamic island
            // Media queries would query the window, but we need to query the inline-size of the row-1

            `@container (max-width: ${DYNAMIC_ISLAND_MIN_CONTENT_WIDTH - 1}px) {`,

              /* Show only the first item in the first group on mobile */
              '.navigation.flex-row:not(.has-mobile-menu-breakpoint) {',
                '.nav-group:first-child .nav-item:not(:first-child),',
                '.nav-group:nth-child(n+2) {',
                  'display: none;',
                '}',
              '}',
              
              /* Align mobile nav items */
              '.nav-group {',
                'flex-grow: 0;',
                '.nav-link {',
                  'min-width: max-content;',
                '}',
              '}',

              /* counter balance the hamburger icon and its outer-spacing */
              '.row-1:not(:has(.row-1-right *)) #topbar-nav.align-mobile-center {',
                'padding-inline-start: calc(var(--input-min-height) + var(--size-2));',
                'justify-content: center;',
                '.nav-group {',
                  'justify-content: center;',
                  'flex-grow: 1;',
                  '.nav-link .button-label {',
                    'padding-inline-start: var(--input-inline-spacing);',
                  '}',
                '}',
              '}',

              // Show mobile settings save/cancel tools, hide desktop tools
              '.row-1-right .desktop-settings-save-cancel {',
                'display: none;',
              '}',
              '.row-1-right .mobile-settings-save-cancel {',
                'display: flex;',
              '}',

            '}',

            `@container (min-width: ${DYNAMIC_ISLAND_MIN_CONTENT_WIDTH}px) {`,
            

              // Hide the mobile drawer toggle on desktop
              '.mobile-drawer-toggle {',
                'display: none;',
              '}',

              // Show the desktop nav items
              '.navigation .nav-group.flex-row .nav-item.desktop-only {',
                'display: inline-flex;',
              '}',

              // Hide the mobile entry editing tools and ellipsis menu button on desktop
              '.mobile-entry-editing-tools, .mobile-ellipsis-menu-button {',
                'display: none;',
              '}',

              // Show the desktop nav items
              '.desktop-ellipsis-menu-button,',
              '.row-1-right .messages-link,',
              '.row-1-right .desktop-start-editing-tool,',
              '.row-1-right .cancel-tool,',
              '.row-1-right .save-tool {',
                'display: flex;',
              '}',

              // On desktop keep only row-1 desktop settings save/cancel tools
              '.row-1-right .desktop-settings-save-cancel {',
                'display: flex;',
              '}',
              '.row-1-right .mobile-settings-save-cancel {',
                'display: none;',
              '}',

            '}',

            // Apply flex-basis: 0 to nav groups only when there's enough space for balanced layout
            `@container (min-width: ${PRECISE_CENTERING_THRESHOLD}px) {`,
              
              // Prevent overflows in the nav-groups
              // The flex-basis: 0 forces all .nav-group elements to start with zero initial size before growing, ensuring they distribute available space equally.
              '.navigation .nav-group {',
                'flex-basis: 0;',
              '}',

            '}',

            /* adjust center position of items in second nav-group when there are three nav-groups by the width of the currently visible tools */
            /** Only center the middle group when there's enough space to avoid visual collisions between groups */
            `@container (min-width: ${PRECISE_CENTERING_THRESHOLD}px) {`,

              '.row-1:has(.row-1-left #topbar-nav .nav-group:nth-child(3)):has(.row-1-right .explore-link) {',
                '--explore-link-offset: var(--menu-item-min-size);',
              '}',
              '.row-1:has(.row-1-left #topbar-nav .nav-group:nth-child(3)):has(.row-1-right .create-entry-tool) {',
                '--create-entry-tool-offset: var(--menu-item-min-size);',
              '}',
              '.row-1:has(.row-1-left #topbar-nav .nav-group:nth-child(3)):has(.row-1-right .account-menu-button) {',
                '--account-menu-offset: var(--menu-item-min-size);',
              '}',
              '.row-1:has(.row-1-left #topbar-nav .nav-group:nth-child(3)):has(.row-1-right .desktop-ellipsis-menu-button) {',
                '--desktop-ellipsis-menu-button-offset: var(--menu-item-min-size);',
              '}',
              '.row-1:has(.row-1-left #topbar-nav .nav-group:nth-child(3)):has(.row-1-right .desktop-start-editing-tool) {',
                '--desktop-start-editing-tool-offset: var(--menu-item-min-size);',
              '}',
              '.row-1:has(.row-1-left #topbar-nav .nav-group:nth-child(3)) .row-1-left .nav-group:nth-child(2) {',
                'margin-inline-end: calc(-1 * (var(--explore-link-offset, 0px) + var(--create-entry-tool-offset, 0px) + var(--account-menu-offset, 0px) + var(--desktop-ellipsis-menu-button-offset, 0px) + var(--desktop-start-editing-tool-offset, 0px)));',
              '}',

            '}',

          '}',

        '}',
      '</style>'
    ].join('')
  });

  const style_tag_custom_css = $derived([
    '<style lang="ignorepreprocess">',
      site_settings.custom_css,
    '</style>'
  ].join(''));

  const style_tag_debug = $derived([
    '<style lang="ignorepreprocess">',
      '* {',
        'outline: 0.1px solid rgba(255, 0, 0, 0.1);',
      '}',
    '</style>'
  ].join(''));

  // disable zooming on iOS text fields
  // TODO: evaluate the alternative: require each input to have a minimum font-size of 16px.
  const add_maximum_scale_to_meta_viewport = () => {
    const el = document.querySelector('meta[name=viewport]');
    
    if (el !== null) {
      let content = el.getAttribute('content');
      let re = /maximum\-scale=[0-9\.]+/g;

      if (re.test(content)) {
        content = content.replace(re, 'maximum-scale=1.0');
      } else {
        content = [content, 'maximum-scale=1.0'].join(', ')
      }

      el.setAttribute('content', content);
    }
  };

  const disable_ios_text_field_zoom = add_maximum_scale_to_meta_viewport;

  const check_is_ios = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Links in running text run their underline on hover (typography.css, `.text a`). The run is
  // a background line that has to land exactly on the underline, which hangs from the baseline -
  // a font's descent above the bottom of the link's box, the only edge a background can be
  // placed from. Measured once per link, the first time it is pointed at: the canvas reports
  // the same font metrics the layout uses. A link without an underline gets no line.
  let metrics_canvas;
  function measure_link_line(event) {
    const link = event.target?.closest?.('.text a');
    if (!link || link.style.getPropertyValue('--link-line')) return;
    const style = getComputedStyle(link);
    if (!style.textDecorationLine.includes('underline')) {
      link.style.setProperty('--link-line', '0px');
      return;
    }
    const context = (metrics_canvas ??= document.createElement('canvas')).getContext('2d');
    context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const descent = context.measureText('x').fontBoundingBoxDescent;
    const thickness = parseFloat(style.textDecorationThickness) || 1;
    const offset = parseFloat(style.textUnderlineOffset) || 0;
    // WebKit draws the underline on the next device pixel down, backgrounds on the nearest one:
    // half a device pixel lower makes both land on the same row (Chromium matches as it is)
    const webkit_rounding = navigator.vendor === 'Apple Computer, Inc.' ? 0.5 / devicePixelRatio : 0;
    link.style.setProperty('--link-line-lift', `${descent - offset - thickness - webkit_rounding}px`);
    link.style.setProperty('--link-line-hide', 'transparent');
    link.style.setProperty('--link-line', `${thickness}px`);
  }

  // Count clicks on links that leave the site (booking, shop, maps, phone, e-mail),
  // so analytics can show where visits end. Only the target host is sent.
  function track_exit(event) {
    if (event.button > 1 || page.data.current_user?.role === 'admin') return;
    const link = event.target?.closest?.('a[href]');
    if (!link) return;
    let link_url;
    try {
      link_url = new URL(link.getAttribute('href'), location.href);
    } catch {
      return;
    }
    const target = link_url.protocol === 'tel:' ? 'tel'
      : link_url.protocol === 'mailto:' ? 'mail'
      : /^https?:$/.test(link_url.protocol) && link_url.host !== location.host ? link_url.hostname
      : null;
    if (!target) return;
    navigator.sendBeacon?.('/api/record-exit', JSON.stringify({
      entry_id: page.data.entry?.entry_id ?? null,
      target
    }));
  }

  // Following a link to another page cross-fades the two pages instead of cutting between them
  // (timing in animations.css). Both snapshots fade in step, so whatever is identical on both
  // pages - the menu bar - holds perfectly still and only the content changes.
  // Not for back/forward: Safari's swipe-back already animates, and a fade on top plays twice.
  // Not for query or hash changes on the same page (calendar periods, search, anchors).
  onNavigate((navigation) => {
    if (
      !document.startViewTransition
      || navigation.type !== 'link'
      || navigation.from?.url.pathname === navigation.to?.url.pathname
      || !(site_settings.typography.animation_intensity > 0)
      || matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;

    return new Promise((resolve) => {
      const transition = document.startViewTransition(async () => {
        // The new page has to be captured at its final scroll position. A site's
        // `html { scroll-behavior: smooth }` would turn SvelteKit's reset to the top into a
        // glide that starts after the capture - the fade would show the new page far down,
        // then scroll it up through its whole length.
        const root_style = document.documentElement.style;
        root_style.scrollBehavior = 'auto';
        resolve();
        try {
          // A cancelled navigation (another click) just ends the fade early. One that never
          // settles (a render error) must not hold the frozen snapshot until the browser's own
          // timeout, so the fade starts with whatever is there after a second.
          await Promise.race([
            navigation.complete,
            new Promise((done) => setTimeout(done, 1000))
          ]);
        } catch {
          // cancelled or failed - nothing to wait for
        } finally {
          root_style.scrollBehavior = '';
        }
      });
      // Skipped transitions (hidden tab, a newer transition) reject `ready` - nothing to do
      transition.ready.catch(() => {});
    });
  });

  onMount(track_screen_height);

  onMount(async () => {
    await load_popover_polyfill();
    
    if (check_is_ios()) {
      disable_ios_text_field_zoom();
    }
  });
</script>

<!-- TODO: We have a duplicate on_paste handler on Text.svelte. We may want one global one only -->
<svelte:window 
  onpaste={on_paste}
  onclick={handle_click_outside}
  onkeydown={handle_escape}
  onfocusin={handle_click_outside}
/>
<!-- Redundant with click ? -->
<!-- ontouchstart={handle_click_outside} -->
<!-- auxclick: middle-click opens the link in a new tab -->
<svelte:document onclick={track_exit} onauxclick={track_exit} onpointerover={measure_link_line} />


{#key site_settings}
  {@html style_tag_font_faces}
  {@html style_tag_colors}
  {@html style_tag_typography_variables}
  {@html style_tag_dynamic_island}
  {@html style_tag_custom_css}
{/key}

{#if dev && env.PUBLIC_SHOW_DEBUG_STYLES === 'true'}
  {@html style_tag_debug}
{/if}

<!-- animations-off: hover motion in components that cannot see the site settings (ButtonLabel) -->
<div class='layout-container' class:animations-off={!(site_settings.typography.animation_intensity > 0)}>
  {@render children()}
</div>

<style>
  /* this allows us to use cqw units in the block grid and avoid the classic scrollbar problem when using "100vw" */
  /* By turning the <body> element into an inline-size query container, 
    the length 100cqw — which is the width of <body> in this case — 
    can be used instead of 100vw to get the desired result. 
    Unlike 100vw, 100cqw becomes smaller when a vertical classic scrollbar appears on the page. */
  /* :global(.layout-container) {
    container-type: inline-size;
  } */
   /* ISSUE: can't use container-type as it causes issues with anything that needs relative positioning e.g. sticky elements */
   /* “The layout containment box establishes an absolute positioning containing block and a fixed positioning containing block.” */
</style>